import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/enhanced-button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { getInviteInfo, acceptInvite, validateSession } from "@/services/auth.service";
import posthog, { registerSession } from "@/lib/posthog";

const newUserSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type NewUserFormValues = z.infer<typeof newUserSchema>;

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const token = searchParams.get("token") ?? "";

  const [isLoading, setIsLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState<{ organizationName: string; email: string } | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  const form = useForm<NewUserFormValues>({
    resolver: zodResolver(newUserSchema),
    defaultValues: { firstName: "", lastName: "", password: "", confirmPassword: "" },
  });

  useEffect(() => {
    if (!token) {
      setInviteError("Missing invite token.");
      return;
    }

    Promise.all([
      getInviteInfo(token).catch((err) => {
        throw err;
      }),
      validateSession(),
    ])
      .then(([info, session]) => {
        setInviteInfo(info);
        setIsLoggedIn(!!session);
        if (session?.user) {
          setUserId(session.user.id);
          posthog.identify(session.user.id, { email: session.user.email });
          registerSession();
        }
      })
      .catch((err) => {
        setInviteError(err?.message || "Invalid or expired invite link.");
      });
  }, [token]);

  const handleJoinAsExistingUser = async () => {
    setIsLoading(true);
    try {
      await acceptInvite(token);
      posthog.capture("invite_accepted", {
        organization_name: inviteInfo?.organizationName,
        new_user: false,
      });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to accept invite.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterAndJoin = async (values: NewUserFormValues) => {
    setIsLoading(true);
    try {
      await acceptInvite(token, {
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
      });
      const session = await validateSession();
      if (session?.user) {
        posthog.identify(session.user.id, { email: session.user.email, firstName: session.user.firstName, lastName: session.user.lastName });
        registerSession();
      }
      posthog.capture("invite_accepted", {
        organization_name: inviteInfo?.organizationName,
        new_user: true,
      });
      navigate("/onboarding/connect", { replace: true });
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to accept invite.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (inviteError) {
      return (
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Invalid invite</h1>
          <p className="text-muted-foreground mb-6">{inviteError}</p>
          <button
            onClick={() => navigate("/")}
            className="text-sm text-muted-foreground underline hover:text-foreground transition-colors"
          >
            Back to sign in
          </button>
        </div>
      );
    }

    if (!inviteInfo || isLoggedIn === null) {
      return (
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading invite...</p>
        </div>
      );
    }

    if (isLoggedIn) {
      return (
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">You've been invited!</h1>
          <p className="text-muted-foreground mb-2">You're invited to join</p>
          <p className="text-xl font-semibold text-foreground mb-6">{inviteInfo.organizationName}</p>
          <Button size="lg" className="w-full" onClick={handleJoinAsExistingUser} disabled={isLoading}>
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Joining...
              </>
            ) : (
              `Join ${inviteInfo.organizationName}`
            )}
          </Button>
        </div>
      );
    }

    return (
      <>
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">You've been invited!</h1>
          <p className="text-muted-foreground mb-1">Create your account to join</p>
          <p className="text-xl font-semibold text-foreground">{inviteInfo.organizationName}</p>
          {inviteInfo.email && (
            <p className="text-sm text-muted-foreground mt-2">Signing up as {inviteInfo.email}</p>
          )}
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleRegisterAndJoin)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="At least 8 characters" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Confirm your password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" size="lg" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating account...
                </>
              ) : (
                "Accept invite & create account"
              )}
            </Button>
          </form>
        </Form>

        <p className="mt-4 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <button
            onClick={() => navigate("/")}
            className="underline hover:text-foreground transition-colors font-medium"
          >
            Sign in
          </button>
        </p>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-xl bg-card/95 backdrop-blur-sm">
          <CardContent className="p-8">
            <div className="mb-6 flex justify-center">
              <img src="/content/logo.png" alt="BlinkBooks Logo" className="w-20 h-20 object-contain" />
            </div>
            {renderContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcceptInvite;
